import React from 'react';
import {
  Smartphone,
  Tablet,
  Monitor,
  Hash,
  Copy,
  Trash2,
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
  Settings,
} from 'lucide-react';
import { PageSection, ComponentStyle } from '../../../types';
import { useSite } from '../../../context/SiteContext';

interface AdvancedTabProps {
  section: PageSection;
  onUpdateStyle: (updates: Partial<ComponentStyle>) => void;
  onDuplicateSection?: () => void;
  onDeleteSection?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

export const AdvancedTab: React.FC<AdvancedTabProps> = ({
  section,
  onUpdateStyle,
  onDuplicateSection,
  onDeleteSection,
  onMoveUp,
  onMoveDown,
}) => {
  const style = section.style || {};
  const responsive = style.responsive || {
    hideOnMobile: false,
    hideOnTablet: false,
    hideOnDesktop: false,
  };

  const handleToggleResponsive = (key: 'hideOnMobile' | 'hideOnTablet' | 'hideOnDesktop') => {
    onUpdateStyle({
      responsive: {
        ...responsive,
        [key]: !responsive[key],
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Device Visibility */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Monitor className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Cihaz Görünürlüğü (Responsive)</h4>
        </div>

        <div className="space-y-2">
          {/* Desktop */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900/60 border border-slate-700/60">
            <div className="flex items-center gap-2.5">
              <Monitor className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-semibold text-slate-200">Masaüstü (Desktop)</span>
            </div>
            <button
              type="button"
              onClick={() => handleToggleResponsive('hideOnDesktop')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition ${
                !responsive.hideOnDesktop
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
              }`}
            >
              {!responsive.hideOnDesktop ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              <span>{!responsive.hideOnDesktop ? 'Görünür' : 'Gizli'}</span>
            </button>
          </div>

          {/* Tablet */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900/60 border border-slate-700/60">
            <div className="flex items-center gap-2.5">
              <Tablet className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-semibold text-slate-200">Tablet</span>
            </div>
            <button
              type="button"
              onClick={() => handleToggleResponsive('hideOnTablet')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition ${
                !responsive.hideOnTablet
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
              }`}
            >
              {!responsive.hideOnTablet ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              <span>{!responsive.hideOnTablet ? 'Görünür' : 'Gizli'}</span>
            </button>
          </div>

          {/* Mobile */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900/60 border border-slate-700/60">
            <div className="flex items-center gap-2.5">
              <Smartphone className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-semibold text-slate-200">Mobil (Telefon)</span>
            </div>
            <button
              type="button"
              onClick={() => handleToggleResponsive('hideOnMobile')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition ${
                !responsive.hideOnMobile
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
              }`}
            >
              {!responsive.hideOnMobile ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              <span>{!responsive.hideOnMobile ? 'Görünür' : 'Gizli'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* HTML Anchor ID */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-2">
        <div className="flex items-center gap-2">
          <Hash className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Bölüm Çapa (Anchor) ID</h4>
        </div>
        <input
          type="text"
          value={style.customId || ''}
          onChange={(e) => onUpdateStyle({ customId: e.target.value.replace(/[^a-zA-Z0-9_-]/g, '') })}
          placeholder="Örn: fiyatlar (Menüden #fiyatlar ile atlamak için)"
          className="w-full bg-slate-900 border border-slate-700 text-xs font-mono text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
        />
        <p className="text-[10px] text-slate-400">
          Menüdeki bir link bu bölüme kaydırılacaksa buradaki ID'yi kullanabilirsiniz.
        </p>
      </div>

      {/* Section Operations */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3">
        <div className="flex items-center gap-2">
          <Settings className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Bölüm İşlemleri</h4>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {onMoveUp && (
            <button
              type="button"
              onClick={onMoveUp}
              className="p-2.5 rounded-xl bg-slate-900 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold flex items-center justify-center gap-2 transition"
            >
              <ArrowUp className="w-3.5 h-3.5" />
              <span>Yukarı Taşı</span>
            </button>
          )}

          {onMoveDown && (
            <button
              type="button"
              onClick={onMoveDown}
              className="p-2.5 rounded-xl bg-slate-900 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold flex items-center justify-center gap-2 transition"
            >
              <ArrowDown className="w-3.5 h-3.5" />
              <span>Aşağı Taşı</span>
            </button>
          )}

          {onDuplicateSection && (
            <button
              type="button"
              onClick={onDuplicateSection}
              className="p-2.5 rounded-xl bg-slate-900 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold flex items-center justify-center gap-2 transition"
            >
              <Copy className="w-3.5 h-3.5" />
              <span>Bölümü Kopyala</span>
            </button>
          )}

          {onDeleteSection && (
            <button
              type="button"
              onClick={onDeleteSection}
              className="p-2.5 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 text-xs font-bold flex items-center justify-center gap-2 transition"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span>Bölümü Sil</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
